/**
 * CF-BONUS-SETTLED-UNAVAILABLE-RESILIENCE-V1 — read classification + automatic retry ladder.
 *
 * Authored 2026-08-19 after PL Telegram #13483 (04:46:24Z) and installed under Scorer's local
 * BUILD/TEST SIGN (04:50Z). Governing source: cumulative R1 `c5fd07bb…` + ACKed R2 overlay
 * `d0bc2817…`, against baseline `a8b836138a3c06ffe2d5a2e98abea5131cb0fd1f`.
 *
 * Pure: no React, no window, no fetch, no timers. Unit-testable in plain Node, same shape as
 * `i18n/core.ts` and `bonus-state.ts`. This is the ONLY place a transport observation becomes a
 * retry class — a caller cannot re-decide it, which is what stops "retry" leaking toward
 * `WithdrawModal`'s fail-closed one-shot guard.
 *
 * Frozen classification (BUILD-R1 §1, Scorer-accepted):
 *
 *   | observation                                                    | class       |
 *   |----------------------------------------------------------------|-------------|
 *   | valid parsed real view                                         | success     |
 *   | auth failure (401)                                             | auth        |
 *   | 408, any 5xx, network failure, promise rejection, sync throw    | transient   |
 *   | 429                                                            | throttled   |
 *   | any other 4xx, malformed 200 body, invalid policy input         | permanent   |
 *
 * ⛔ `throttled` is NOT `transient`. A 429 is never answered with more requests — that is the whole
 * point of separating the two classes, and settle row 6 (automatic retry) cannot match it.
 */

/** The five disjoint outcomes of one physical read. */
export type ReadClass = 'success' | 'transient' | 'throttled' | 'auth' | 'permanent';

/**
 * What the transport observed. Deliberately describes the OBSERVATION, not the verdict — the
 * verdict is this module's job, so there is exactly one place status→class is decided.
 */
export type ReadObservation =
  | { kind: 'ok' }                    // 2xx whose body parsed to a valid view
  | { kind: 'http'; status: number }  // any non-2xx status
  | { kind: 'network' }               // fetch could not complete
  | { kind: 'rejected' }              // the promise rejected
  | { kind: 'threw' }                 // a synchronous throw escaped
  | { kind: 'malformed_body' };       // 2xx whose body is not a valid v1 contract body

/** HTTP statuses that are transient despite living in the 4xx range. */
const TRANSIENT_4XX = 408;
const AUTH_STATUS = 401;
const THROTTLED_STATUS = 429;

/**
 * Classify one read.
 *
 * Takes `unknown` on purpose: "invalid policy input" is a row in the frozen table, and the only
 * safe verdict for input we cannot recognise is `permanent`. Classifying garbage as `transient`
 * would arm the retry ladder against a fault that will never clear.
 */
export function classifyRead(observation: unknown): ReadClass {
  if (typeof observation !== 'object' || observation === null) return 'permanent';

  const kind = (observation as { kind?: unknown }).kind;
  if (typeof kind !== 'string') return 'permanent';

  switch (kind) {
    case 'ok':
      return 'success';

    // Transport never reached a verdict. These are the genuinely retryable faults.
    case 'network':
    case 'rejected':
    case 'threw':
      return 'transient';

    // A 200 we cannot trust is a contract failure, not an absence of bonus (A1) and not a blip.
    case 'malformed_body':
      return 'permanent';

    case 'http': {
      const status = (observation as { status?: unknown }).status;
      // A non-integer status is invalid policy input, not a 5xx. Fail closed.
      if (typeof status !== 'number' || !Number.isInteger(status)) return 'permanent';

      if (status === AUTH_STATUS) return 'auth';
      if (status === THROTTLED_STATUS) return 'throttled';
      if (status === TRANSIENT_4XX) return 'transient';
      if (status >= 500 && status <= 599) return 'transient';
      if (status >= 400 && status <= 499) return 'permanent';

      // Anything outside 4xx/5xx reaching here (a 3xx, a 0, a 999) is not a case the contract
      // describes. Unrecognised is `permanent` — never retried on a guess.
      return 'permanent';
    }

    default:
      return 'permanent';
  }
}

/**
 * The automatic ladder: at most four attempts, spaced 1s / 3s / 9s after attempts 1, 2 and 3.
 * These are the initial-establishment retries, distinct from event probes and their 1,500 ms floor.
 */
export const AUTOMATIC_RETRY_DELAYS_MS: readonly number[] = [1000, 3000, 9000];

/** Attempt 4 is the last. A failure at attempt 4 latches visible exhaustion; no timer is armed. */
export const MAX_AUTOMATIC_ATTEMPTS = AUTOMATIC_RETRY_DELAYS_MS.length + 1; // 4

/**
 * Minimum spacing between owner-initiated EVENT probes.
 *
 * 1500 ms, not 1000 ms: the `/bonus` bucket refills at 1 token/s continuously, so a 1000 ms floor
 * consumes the entire refill and leaves zero surplus for the lifecycle reads and WithdrawModal's
 * deliberately uncooldowned one-shot. At 1500 ms sustained event probes take 0.667/s, reserving
 * ~20 tokens/minute of headroom. ⚠️ That is a LONG-RUN BUDGET, not a burst guarantee, and it is
 * not a claim of mathematical non-exhaustion.
 */
export const EVENT_MIN_INTERVAL_MS = 1500;

/**
 * Is this class eligible for the AUTOMATIC ladder?
 *
 * ⛔ `transient` only. `throttled` is excluded by construction — answering a rate limit with more
 * requests is the failure this separation exists to prevent. `auth` and `permanent` will not clear
 * by waiting, so retrying them only spends tokens.
 */
export function isAutomaticallyRetryable(cls: ReadClass): boolean {
  return cls === 'transient';
}

/**
 * Delay before the next automatic attempt, given how many automatic attempts have already been
 * spent in this generation.
 *
 * Returns `null` when the ladder is exhausted — the caller must then latch exhaustion rather than
 * arm a timer. `null` is also the answer for invalid input: no retry is the fail-closed direction.
 */
export function nextAutomaticDelayMs(attemptsSpent: number): number | null {
  if (typeof attemptsSpent !== 'number' || !Number.isInteger(attemptsSpent)) return null;
  if (attemptsSpent < 1) return null;
  if (attemptsSpent > AUTOMATIC_RETRY_DELAYS_MS.length) return null;
  return AUTOMATIC_RETRY_DELAYS_MS[attemptsSpent - 1];
}
